import { parse, tokenizer } from "acorn";
import { isDummy, parse as parse$1 } from "acorn-loose";
import escodegen from "escodegen";
import evalESTreeExpr from "eval-estree-expression";
import * as walk from "acorn-walk";
import assertNever from "assert-never";

//#region src/errors.ts
var ExpressionError = class ExpressionError extends Error {
	/**
	* The location of the error in the parsed expression.
	*/
	location;
	/**
	* The expression token with the error
	*/
	token;
	constructor(message, loc, token) {
		super(message);
		if (Error.captureStackTrace) Error.captureStackTrace(this, ExpressionError);
		this.name = "ExpressionError";
		this.location = loc;
		this.token = token;
	}
};

//#endregion
//#region src/utils.ts
/**
* Format the result value of an expression for display as a string.
*/
function formatExpressionResult(value, defaultValue = "") {
	if (value === void 0 || value === null) return defaultValue;
	if (typeof value === "string") return value;
	if (typeof value === "number" || typeof value === "boolean") return value.toString();
	return defaultValue;
}
/**
* Filter function to exclude `null` values
*/
function filterOutNullable(value) {
	return !!value;
}

//#endregion
//#region src/input-values.ts
/**
* Infers a default inputValues object based on the JSON schema of an object.
*/
function inferDefaultInputValuesFromObjectJSONSchema(schema) {
	if (schema.type !== "object" || !schema.properties) throw new Error(`Expected schema of object to be provided: ${schema.type}`);
	const result = {};
	for (const [key, propertySchema] of Object.entries(schema.properties)) {
		if (typeof propertySchema === "boolean") continue;
		result[key] = inferDefaultInputValueFromJSONSchema(propertySchema);
	}
	return result;
}
function inferDefaultInputValueFromJSONSchema(schema) {
	switch (schema.type) {
		case "object": return inferDefaultInputValuesFromObjectJSONSchema(schema);
		case "array":
			if (schema.items && Array.isArray(schema.items)) return schema.items.map((itemSchema) => {
				if (typeof itemSchema === "boolean") return false;
				return inferDefaultInputValueFromJSONSchema(itemSchema);
			});
			return [];
		case "string":
		case "number":
		case "integer":
		case "boolean":
		case "null": return inferDefaultInputValueFromPrimitive(schema);
		default: throw new Error(`Unsupported schema type: ${schema.type}`);
	}
}
function inferDefaultInputValueFromPrimitive(schema) {
	switch (schema.type) {
		case "boolean": return true;
		case "number":
		case "integer": return 1234;
		case "string": return (schema.enum?.filter(filterOutNullable))?.[0] ?? "default";
		case "null": return null;
		default: throw new Error(`Unsupported schema type: ${schema.type}`);
	}
}

//#endregion
//#region src/symbols/types.ts
let SymbolType = /* @__PURE__ */ function(SymbolType$1) {
	SymbolType$1["Boolean"] = "boolean";
	SymbolType$1["Number"] = "number";
	SymbolType$1["String"] = "string";
	SymbolType$1["Object"] = "object";
	SymbolType$1["Array"] = "array";
	SymbolType$1["Function"] = "function";
	SymbolType$1["Union"] = "union";
	SymbolType$1["Undefined"] = "undefined";
	SymbolType$1["Null"] = "null";
	return SymbolType$1;
}({});
function resolveSymbolDef(symbol) {
	switch (symbol.type) {
		case SymbolType.String: return symbol;
		case SymbolType.Number: return symbol;
		case SymbolType.Boolean: return symbol;
		case SymbolType.Array: return symbol;
		case SymbolType.Object: return symbol;
		case SymbolType.Function: return symbol;
		case SymbolType.Union: return symbol;
		case SymbolType.Undefined: return symbol;
		case SymbolType.Null: return symbol;
		default: throw new Error(`Unknown symbol type: ${symbol.type}`);
	}
}

//#endregion
//#region src/symbols/symbols.ts
function SymbolBoolean(args = {}) {
	return {
		type: SymbolType.Boolean,
		...args
	};
}
function SymbolNumber(args = {}) {
	return {
		type: SymbolType.Number,
		...args
	};
}
function SymbolString(args = {}) {
	return createSymbolWithPropertiesAndMethods(SymbolType.String, args);
}
function SymbolObject(args) {
	return {
		type: SymbolType.Object,
		...args
	};
}
function SymbolArray(args) {
	return createSymbolWithPropertiesAndMethods(SymbolType.Array, args);
}
function SymbolFunction(args) {
	return {
		type: SymbolType.Function,
		...args
	};
}
function OptionalFunctionArg(optionalArg) {
	return {
		...optionalArg,
		optional: true
	};
}
function SymbolUnion(args) {
	return {
		type: SymbolType.Union,
		...args
	};
}
function SymbolUndefined(args = {}) {
	return {
		type: SymbolType.Undefined,
		...args
	};
}
function SymbolNull(args = {}) {
	return {
		type: SymbolType.Null,
		...args
	};
}
function createSymbolWithPropertiesAndMethods(type, args) {
	const symbol = {
		type,
		...args
	};
	Object.defineProperty(symbol, "properties", { get() {
		if (symbol.type === SymbolType.Array) return isArraySymbol(symbol) ? StandardLibrary[SymbolType.Array]?.(symbol).properties : {};
		return StandardLibrary[symbol.type]?.properties || {};
	} });
	Object.defineProperty(symbol, "methods", { get() {
		if (symbol.type === SymbolType.Array) return isArraySymbol(symbol) ? StandardLibrary[SymbolType.Array]?.(symbol).methods : [];
		return StandardLibrary[symbol.type]?.methods || [];
	} });
	return symbol;
}
const StandardLibrary = {
	[SymbolType.String]: {
		properties: { length: SymbolNumber({
			name: "length",
			description: "The length data property of a String value contains the length of the string in UTF-16 code units.",
			link: "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/length"
		}) },
		methods: [
			SymbolFunction({
				name: "at",
				description: `Takes an integer value and returns the item at that index, allowing for positive and negative integers.
            Negative integers count back from the last item in the string.`,
				link: "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/at",
				args: [SymbolNumber({
					name: "index",
					description: "The index (position) of the string character to be returned"
				})],
				returns: SymbolUnion({
					description: `A String consisting of the single UTF-16 code unit located at the specified position.
                Returns undefined if the given index can not be found.`,
					members: [SymbolString(), SymbolUndefined()]
				})
			}),
			SymbolFunction({
				name: "endsWith",
				description: `Returns true if the sequence of elements of searchString converted to a String is the same as the corresponding 
            elements of this object (converted to a String) starting at endPosition – length(this). Otherwise returns false.`,
				link: "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/endsWith",
				args: [SymbolString({
					name: "searchString",
					description: `The characters to be searched for at the end of str. Cannot be a regex.
                    All values that are not regexes are coerced to strings, so omitting it or passing undefined causes endsWith() to search for 
                    the string "undefined", which is rarely what you want.`
				}), OptionalFunctionArg(SymbolNumber({
					name: "endPosition",
					description: `The end position at which searchString is expected to be found 
                    (the index of searchString's last character plus 1). Defaults to str.length.`
				}))],
				returns: SymbolBoolean({ description: `true if the given characters are found at the end of the string, including when searchString is an empty string; 
                otherwise, false.` })
			}),
			SymbolFunction({
				name: "includes",
				description: `Returns true if searchString appears as a substring of the result of converting this object to a String, at one or more positions 
            that are greater than or equal to position; otherwise, returns false.`,
				link: "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/includes",
				args: [SymbolString({
					name: "searchString",
					description: `A string to be searched for within str. Cannot be a regex. All values that are not regexes are coerced to strings, so omitting it 
                or passing undefined causes includes() to search for the string "undefined", which is rarely what you want.`
				}), OptionalFunctionArg(SymbolNumber({
					name: "position",
					description: "The position within the string at which to begin searching for searchString. (Defaults to 0.)"
				}))],
				returns: SymbolBoolean({ description: `true if the search string is found anywhere within the given string, including when searchString is an empty string; 
                otherwise, false.` })
			})
		]
	},
	[SymbolType.Array]: (arraySymbolDef) => ({
		properties: { length: SymbolNumber({
			name: "length",
			description: `The length data property of an Array instance represents the number of elements in that array.
                The value is an unsigned, 32-bit integer that is always numerically greater than the highest index in the array.`,
			link: "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/length"
		}) },
		methods: [
			SymbolFunction({
				name: "at",
				description: `Takes an integer value and returns the item at that index, allowing for positive and negative integers. 
                Negative integers count back from the last item in the array.`,
				link: "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/at",
				args: [SymbolNumber({
					name: "index",
					description: `Zero-based index of the array element to be returned, converted to an integer. 
                        Negative index counts back from the end of the array — if index < 0, index + array.length is accessed.`
				})],
				returns: SymbolUnion({
					description: `The element in the array matching the given index. Always returns undefined if index < -array.length or index >= array.length 
                    without attempting to access the corresponding property.`,
					members: [arraySymbolDef.items, SymbolUndefined()]
				})
			}),
			SymbolFunction({
				name: "includes",
				description: "Determines whether an array includes a certain value among its entries, returning true or false as appropriate.",
				link: "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/includes",
				args: [{
					...arraySymbolDef.items,
					name: "searchElement",
					description: "The value to be searched for within the array."
				}, OptionalFunctionArg(SymbolNumber({
					name: "fromIndex",
					description: "The position within the string at which to begin searching for searchString. (Defaults to 0.)"
				}))],
				returns: SymbolBoolean({ description: "true if the value searchElement is found within the array (or the part of the array indicated by the index fromIndex, if specified)." })
			}),
			SymbolFunction({
				name: "some",
				description: "Tests whether at least one element in the array passes the test implemented by the provided function.",
				link: "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/some",
				args: [SymbolFunction({
					name: "callback",
					description: "A function that tests each element of the array.",
					args: [{
						...arraySymbolDef.items,
						name: "element",
						description: "The current element being processed in the array."
					}],
					returns: SymbolBoolean({ description: "true if the callback function returns a truthy value for at least one element in the array." })
				})],
				returns: SymbolBoolean({ description: "true if the callback function returns a truthy value for at least one element in the array." })
			}),
			SymbolFunction({
				name: "every",
				description: "Tests whether all elements in the array pass the test implemented by the provided function.",
				link: "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/every",
				args: [SymbolFunction({
					name: "callback",
					description: "A function that tests each element of the array.",
					args: [{
						...arraySymbolDef.items,
						name: "element",
						description: "The current element being processed in the array."
					}],
					returns: SymbolBoolean({ description: "true if the callback function returns a truthy value for all elements in the array." })
				})],
				returns: SymbolBoolean({ description: "true if the callback function returns a truthy value for all elements in the array." })
			})
		]
	})
};
function isArraySymbol(symbol) {
	return symbol.type === SymbolType.Array;
}

//#endregion
//#region src/symbols/symbols-table.ts
var SymbolError = class SymbolError extends Error {
	constructor(message) {
		super(message);
		if (Error.captureStackTrace) Error.captureStackTrace(this, SymbolError);
		this.name = "SymbolError";
	}
};
var SymbolsTable = class SymbolsTable {
	/**
	* Internal table that keeps track of all symbols reference.
	*/
	#table;
	/**
	* Internal table that keep track of the raw symbols definitions.
	*/
	#rawSymbols;
	constructor(initialContext = {}) {
		this.#table = {};
		this.#rawSymbols = {};
		this.addSymbols(initialContext);
	}
	/**
	* Create a new symbols table by merging the current one with the provided one.
	*/
	merge(other) {
		return new SymbolsTable({
			...this.#rawSymbols,
			...other.#rawSymbols
		});
	}
	toString() {
		return JSON.stringify(this.#table, null, 2);
	}
	/**
	* Infer the symbol of a value and generate the appropriate symbol definition.
	*/
	static inferSymbolFromValue(value, name) {
		if (Array.isArray(value)) {
			if (value.length === 0) return SymbolArray({ items: SymbolUndefined() });
			const firstItemSymbol = SymbolsTable.inferSymbolFromValue(value.at(0));
			if (value.length > 1) {
				const secondItemSymbol = SymbolsTable.inferSymbolFromValue(value.at(1));
				if (firstItemSymbol.type !== secondItemSymbol.type) throw new SymbolError("Array with mixin items types are not supported");
			}
			return SymbolArray({
				name,
				items: firstItemSymbol
			});
		}
		if (typeof value === "undefined") return SymbolUndefined({ name });
		if (value === null) return SymbolNull({ name });
		const valueType = typeof value;
		switch (valueType) {
			case "string": return SymbolString({ name });
			case "number": return SymbolNumber({ name });
			case "boolean": return SymbolBoolean({ name });
			case "object": {
				const properties = Object.entries(value).reduce((prev, [name$1, val]) => {
					prev[name$1] = SymbolsTable.inferSymbolFromValue(val, name$1);
					return prev;
				}, {});
				return SymbolObject({
					name,
					properties,
					methods: []
				});
			}
			default: throw new SymbolError(`Unsupported symbol type ${valueType}`);
		}
	}
	/**
	* Infer a table of symbol based on a JSON schema object describing it.
	*/
	static inferSymbolFromJSONSchema(schema, name) {
		switch (schema.type) {
			case "string": return SymbolString({
				name,
				...schema.description ? { description: schema.description } : {},
				...schema.enum ? { enum: schema.enum.filter(filterOutNullable).map((enumValue) => enumValue.toString()) } : {}
			});
			case "number":
			case "integer": return SymbolNumber({
				name,
				...schema.description ? { description: schema.description } : {}
			});
			case "boolean": return SymbolBoolean({
				name,
				...schema.description ? { description: schema.description } : {}
			});
			case "null": return SymbolNull({
				name,
				...schema.description ? { description: schema.description } : {}
			});
			case "object": return SymbolsTable.#buildObjectSymbolFromJSONSchemaObject(schema, name);
			case "array": return SymbolsTable.#buildArraySymbolFromJSONSchemaArray(schema, name);
			default: throw new Error(`Unsupported schema type: ${schema.type}`);
		}
	}
	static #buildObjectSymbolFromJSONSchemaObject(schema, name) {
		const properties = {};
		if (schema.properties) Object.entries(schema.properties).forEach(([propertyName, propertySchema]) => {
			properties[propertyName] = SymbolsTable.inferSymbolFromJSONSchema(propertySchema, propertyName);
		});
		return SymbolObject({
			name,
			properties,
			...schema.description ? { description: schema.description } : {},
			methods: []
		});
	}
	static #buildArraySymbolFromJSONSchemaArray(schema, name) {
		if (schema.items) {
			const itemSymbol = SymbolsTable.inferSymbolFromJSONSchema(schema.items, `${name || ""}_item`);
			return SymbolArray({
				name,
				...schema.description ? { description: schema.description } : {},
				items: itemSymbol
			});
		}
		return SymbolArray({
			name,
			...schema.description ? { description: schema.description } : {},
			items: SymbolUndefined()
		});
	}
	generateSymbolRefPath(path) {
		return path.join(".");
	}
	/**
	* Add a single symbol to the table at the provided path.
	*/
	addSymbol(path, definition, raw = false) {
		const fullPath = this.generateSymbolRefPath(path);
		const parentPath = path.slice(0, -1).join(".");
		if (this.#table[fullPath]) throw new SymbolError(`Symbol "${fullPath}" already exists.`);
		if (raw) this.#rawSymbols[fullPath] = definition;
		this.#table[fullPath] = {
			definition: resolveSymbolDef(definition),
			ref: fullPath,
			parentRef: parentPath || void 0,
			childrenRefs: []
		};
		if (parentPath && this.#table[parentPath]) {
			if (this.#table[parentPath].childrenRefs) this.#table[parentPath].childrenRefs.push(fullPath);
		}
		if (isSymbolWithProperties(definition)) Object.entries(definition.properties).forEach(([propKey, propSymbol]) => {
			if (isObjectSymbol(propSymbol)) this.addSymbols({ [propKey]: propSymbol }, path, false);
			else this.addSymbol([...path, propKey], propSymbol, false);
		});
		if (isSymbolWithMethods(definition)) definition.methods.forEach((methodSymbol) => {
			this.addSymbol([...path, methodSymbol.name], methodSymbol, false);
		});
	}
	/**
	* Add the provided object of symbols definitions to the symbol table.
	*/
	addSymbols(symbols, prefix = [], raw = true) {
		for (const [key, symbolDef] of Object.entries(symbols)) {
			const path = [...prefix, key];
			this.addSymbol(path, symbolDef, raw);
		}
	}
	/**
	* Get a symbol's information using its path in the table.
	*/
	getSymbolInfo(path) {
		const key = Array.isArray(path) ? path.join(".") : path;
		const info = this.#table[key];
		return info ? typedSymbolInfo(info) : void 0;
	}
	/**
	* Get all symbol keys matching the pattern defined by the provided path.
	*/
	getMatchingSymbolsKeys(path) {
		const wildcardRegex = /* @__PURE__ */ new RegExp(`^${path.map((segment) => {
			if (segment.includes("*")) return `${segment.split("*")[0]}([^.]+)?`;
			return segment;
		}).join("\\.")}$`);
		return Object.keys(this.#table).filter((key) => wildcardRegex.test(key)).filter(filterOutNullable);
	}
};
function isObjectSymbol(symbol) {
	return symbol.type === SymbolType.Object;
}
function isSymbolWithProperties(symbol) {
	return symbol.type === SymbolType.Object || symbol.type === SymbolType.Array || symbol.type === SymbolType.String;
}
function isSymbolWithMethods(symbol) {
	return symbol.type === SymbolType.Object || symbol.type === SymbolType.Array || symbol.type === SymbolType.String;
}
function typedSymbolInfo(info) {
	const definition = resolveSymbolDef(info.definition);
	return {
		...info,
		definition
	};
}

//#endregion
//#region src/types.ts
const SUPPORTED_BINARY_OPERATORS = [
	{
		operator: "==",
		description: "Checks whether two values are equal.",
		link: "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Equality"
	},
	{
		operator: "!=",
		description: "Checks whether two values are unequal.",
		link: "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Inequality"
	},
	{
		operator: "===",
		description: "Checks whether two values are equal (strict comparison).",
		link: "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Strict_equality"
	},
	{
		operator: "!==",
		description: "Checks whether two values are unequal (strict comparison).",
		link: "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Strict_inequality"
	},
	{
		operator: "<",
		description: "Checks if the left value is less than the right value.",
		link: "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Less_than"
	},
	{
		operator: "<=",
		description: "Checks if the left value is less than or equal to the right value.",
		link: "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Less_than_or_equal"
	},
	{
		operator: ">",
		description: "Checks if the left value is greater than the right value.",
		link: "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Greater_than"
	},
	{
		operator: ">=",
		description: "Checks if the left value is greater than or equal to the right value.",
		link: "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Greater_than_or_equal"
	},
	{
		operator: "in",
		description: "Checks if a property exists in an object or if a value is in an array.",
		link: "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/in"
	}
];
const SUPPORTED_LOGICAL_OPERATORS = [{
	operator: "&&",
	description: "Logical AND operator; returns true if both operands are true.",
	link: "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Logical_AND"
}, {
	operator: "||",
	description: "Logical OR operator; returns true if at least one operand is true.",
	link: "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Logical_OR"
}];
const SUPPORTED_CONDITIONAL_OPERATORS = [{
	operator: "?",
	description: "Conditional (ternary) operator; returns one of two values based on a condition.",
	link: "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Conditional_Operator"
}];

//#endregion
//#region src/autocomplete.ts
var AutoComplete = class {
	#parser;
	#logger;
	constructor(parser, logger = console) {
		this.#parser = parser;
		this.#logger = logger;
	}
	/**
	* Generates autocomplete suggestions based on the input expression and cursor offset position.
	*/
	getSuggestions(expr, cursorOffset, context) {
		if (!expr.length) return [];
		try {
			const { result, invalidNodes } = this.#parser.parse(expr, { loose: true });
			const nodeAtCursorFound = walk.findNodeAround(result, cursorOffset, (_type, node$1) => isNodeAtCursor(node$1, cursorOffset));
			if (!nodeAtCursorFound) {
				if (cursorOffset > result.end) {
					const ast = invalidNodes.length > 0 ? invalidNodes[0]?.expression : result;
					if (!ast) return [];
					const lastNodeFound = walk.findNodeAround(ast, ast.end, (_type, node$2) => isNodeAtCursor(node$2, ast.end));
					if (!lastNodeFound) return [];
					const { node: node$1 } = lastNodeFound;
					if (!isAnyNode(node$1)) throw Error(`Unexpected node type ${node$1.type}`);
					return this.getOperatorSuggestionsForNode(ast, node$1, result.end, context);
				}
				return [];
			}
			const { node } = nodeAtCursorFound;
			if (!isAnyNode(node)) throw Error(`Unexpected node type ${node.type}`);
			return this.getSuggestionsForNode(result, node, expr, cursorOffset, context);
		} catch (error) {
			this.#logger.error("Error while computing autocomplete suggestions", error);
			return [];
		}
	}
	/**
	* Provides autocomplete suggestions for a specific node in the AST.
	*/
	getSuggestionsForNode(ast, node, expr, cursorOffset, context) {
		let inferNode = node;
		if (node.type === "Identifier" || node.type === "Literal") {
			const parent = findParentNode(node, ast);
			inferNode = parent && ![
				"ExpressionStatement",
				"LogicalExpression",
				"ConditionalExpression"
			].includes(parent.type) ? parent : node;
		}
		switch (inferNode.type) {
			case "Identifier":
			case "MemberExpression": {
				const pathParts = this.getSymbolsPathPartsForMemberExpressionNode(inferNode, cursorOffset);
				const candidatesKeys = context.getMatchingSymbolsKeys(pathParts);
				const suggestions = [];
				for (const candidate of candidatesKeys) {
					const symbolInfo = context.getSymbolInfo(candidate);
					if (symbolInfo) suggestions.push({
						type: "symbol",
						symbol: symbolInfo
					});
				}
				if (suggestions.length === 1) return pathParts.at(-1)?.replace(/\*$/, "") !== suggestions[0]?.symbol.definition.name ? suggestions : [];
				return suggestions;
			}
			case "AssignmentExpression":
			case "UnaryExpression": return this.getOperatorSuggestionsForNode(ast, inferNode, cursorOffset, context);
			case "BinaryExpression": {
				const { left, right } = inferNode;
				const isOperatorBinaryOp = SUPPORTED_BINARY_OPERATORS.some((op) => op.operator === inferNode.operator);
				const operatorOffset = expr.indexOf(inferNode.operator, left.end) + inferNode.operator.length;
				if (cursorOffset > operatorOffset && isOperatorBinaryOp && isNodeAtCursor(right, cursorOffset)) return this.getLiteralValueSuggestionsForNode(inferNode, cursorOffset, context);
				return this.getOperatorSuggestionsForNode(ast, inferNode, cursorOffset, context);
			}
			case "ConditionalExpression": {
				const { consequent, alternate } = inferNode;
				if (isNodeAtCursor(consequent, cursorOffset)) return this.getSuggestionsForNode(ast, consequent, expr, cursorOffset, context);
				if (isNodeAtCursor(alternate, cursorOffset)) return this.getSuggestionsForNode(ast, alternate, expr, cursorOffset, context);
			}
		}
		return [];
	}
	/**
	* Return a path corresponding to the MemberExpression node that can be used to lookup matching symbols in the symbol table.
	*/
	getSymbolsPathPartsForMemberExpressionNode(node, cursorOffset, options) {
		const withWildcardMatches = options?.withWildcardMatches ?? true;
		const pathParts = [];
		switch (node.type) {
			case "MemberExpression":
				{
					const memberProperty = node.property;
					if (!isSupportedMemberProperty(memberProperty)) return [];
					const propertyPathPart = this.getSymbolsPathPartFromMemberProperty(memberProperty, cursorOffset);
					if (propertyPathPart) pathParts.push(withWildcardMatches ? `${propertyPathPart}*` : propertyPathPart);
					let parent = node.object;
					while (parent) {
						const parentProperty = "property" in parent ? parent.property : parent;
						const parentPropertyPathPart = isSupportedMemberProperty(parentProperty) ? this.getSymbolsPathPartFromMemberProperty(parentProperty, cursorOffset) : void 0;
						if (parentPropertyPathPart) pathParts.unshift(parentPropertyPathPart);
						parent = "object" in parent ? parent.object : void 0;
					}
				}
				break;
			case "Identifier": {
				const propertyPathPart = this.getSymbolsPathPartFromMemberProperty(node, cursorOffset);
				if (propertyPathPart) pathParts.push(withWildcardMatches ? `${propertyPathPart}*` : propertyPathPart);
				break;
			}
			default: assertNever(node);
		}
		return pathParts;
	}
	/**
	* Return a part of a symbol path corresponding to a MemberExpression node property.
	*/
	getSymbolsPathPartFromMemberProperty(node, cursorOffset) {
		switch (node.type) {
			case "Identifier":
				if (isDummy(node)) return "*";
				return isNodeAtCursor(node, cursorOffset) ? node.name.slice(0, cursorOffset) : node.name;
			case "Literal": {
				if (isDummy(node) || !node.value) return "*";
				const value = String(node.value);
				return isNodeAtCursor(node, cursorOffset) ? value.slice(0, cursorOffset) : value;
			}
			default: assertNever(node);
		}
	}
	/**
	* Provides autocomplete literal value suggestions for a specific node in the AST.
	*/
	getLiteralValueSuggestionsForNode(node, cursorOffset, context) {
		const { left, right } = node;
		if (left.type !== "MemberExpression" && left.type !== "Identifier") return [];
		if (right.type !== "Identifier" && right.type !== "Literal") return [];
		const leftSymbolPath = this.getSymbolsPathPartsForMemberExpressionNode(left, cursorOffset, { withWildcardMatches: false });
		const isLeftComputedMember = left.type === "MemberExpression" && left.computed;
		const leftSymbolInfo = context.getSymbolInfo(isLeftComputedMember ? leftSymbolPath.slice(0, -1) : leftSymbolPath);
		if (!leftSymbolInfo) return [];
		switch (leftSymbolInfo.definition.type) {
			case SymbolType.Boolean: {
				const suggestions = [{
					type: "literal-value",
					value: {
						kind: "direct",
						type: SymbolType.Boolean,
						data: true
					}
				}, {
					type: "literal-value",
					value: {
						kind: "direct",
						type: SymbolType.Boolean,
						data: false
					}
				}];
				return isDummy(right) ? suggestions : suggestions.filter((literalValue) => {
					const literalValueString = String(literalValue.value.data);
					const rightNodeValue = right.type === "Identifier" ? right.name : right.raw ?? "";
					return literalValueString !== rightNodeValue && literalValueString.startsWith(rightNodeValue);
				});
			}
			case SymbolType.String: {
				if (!leftSymbolInfo.definition.enum) return [];
				const rightNodeValue = (() => {
					if (right.type === "Identifier") return right.name;
					const nodeValue = right.raw ?? "";
					if (/^(['"])(.*)\1$/.test(nodeValue)) return null;
					return nodeValue.replaceAll(/["']/g, "");
				})();
				if (rightNodeValue === null) return [];
				return (isDummy(right) ? leftSymbolInfo.definition.enum : leftSymbolInfo.definition.enum.filter((enumValue) => enumValue.startsWith(rightNodeValue))).map((value) => ({
					type: "literal-value",
					value: {
						kind: "direct",
						type: SymbolType.String,
						data: value
					}
				}));
			}
			case SymbolType.Array: return isLeftComputedMember ? [{
				type: "literal-value",
				value: {
					kind: "in-array",
					srcSymbol: leftSymbolInfo.definition,
					matchedLiteralString: !isDummy(right) ? right.type === "Identifier" ? right.name : right.raw ?? "" : ""
				}
			}] : [];
			default: return [];
		}
	}
	/**
	* Provides autocomplete operator suggestions for a specific node in the AST.
	*/
	getOperatorSuggestionsForNode(ast, node, cursorOffset, context) {
		if (node.type === "Literal") {
			if (findParentNode(node, ast)?.type === "BinaryExpression") return [...SUPPORTED_LOGICAL_OPERATORS, ...SUPPORTED_CONDITIONAL_OPERATORS].map((op) => ({
				type: "operator",
				...op
			}));
			const literalSymbol = SymbolsTable.inferSymbolFromValue(node.raw);
			return this.getOperatorSuggestionsForSymbol(literalSymbol);
		}
		let inferNode = node;
		if (node.type === "Identifier") {
			const parent = findParentNode(node, ast);
			inferNode = parent && parent.type !== "ExpressionStatement" ? parent : node;
		}
		switch (inferNode.type) {
			case "MemberExpression": {
				const pathParts = this.getSymbolsPathPartsForMemberExpressionNode(inferNode, cursorOffset, { withWildcardMatches: false });
				const symbolInfo = context.getSymbolInfo(pathParts);
				return symbolInfo ? this.getOperatorSuggestionsForSymbol(symbolInfo.definition) : [];
			}
			case "AssignmentExpression":
			case "BinaryExpression":
			case "UnaryExpression": {
				const operator = inferNode.operator;
				return [...SUPPORTED_BINARY_OPERATORS, ...SUPPORTED_LOGICAL_OPERATORS].filter((op) => op.operator.startsWith(operator)).filter((op) => op.operator !== operator).map((op) => ({
					type: "operator",
					...op
				}));
			}
			default: return [];
		}
	}
	/**
	* Provides autocomplete operator suggestions based on the type of a symbol.
	*/
	getOperatorSuggestionsForSymbol(symbol) {
		switch (symbol.type) {
			case SymbolType.Number:
			case SymbolType.Boolean:
			case SymbolType.Null:
			case SymbolType.Undefined:
			case SymbolType.Object:
			case SymbolType.Array: {
				const equalityOps = SUPPORTED_BINARY_OPERATORS.slice(0, 4);
				return (symbol.type === SymbolType.Boolean ? [
					...equalityOps,
					...SUPPORTED_LOGICAL_OPERATORS,
					...SUPPORTED_CONDITIONAL_OPERATORS
				] : equalityOps).map((op) => ({
					type: "operator",
					...op
				}));
			}
			case SymbolType.String: return SUPPORTED_BINARY_OPERATORS.map((op) => ({
				type: "operator",
				...op
			}));
			case SymbolType.Function: return this.getOperatorSuggestionsForSymbol(symbol.returns);
			case SymbolType.Union: return symbol.members.reduce((prev, cur) => {
				prev.push(...this.getOperatorSuggestionsForSymbol(cur));
				return prev;
			}, []);
			default: assertNever(symbol);
		}
	}
};
/**
* Finds the parent of child node in the provided AST.
*/
function findParentNode(child, ast) {
	let foundParent;
	walk.ancestor(ast, { [child.type]: (node, _state, ancestors) => {
		if (node.start === child.start && node.end === child.end) foundParent = ancestors[ancestors.length - 2];
	} });
	return foundParent;
}
function isSupportedMemberProperty(node) {
	return node.type === "Identifier" || node.type === "Literal";
}
function isNodeAtCursor(node, cursorOffset) {
	return cursorOffset >= node.start && cursorOffset <= node.end;
}
function isAnyNode(node) {
	return "type" in node;
}

//#endregion
//#region src/template.ts
/**
* Parse a template string containing `{{ expression }}` placeholders.
*/
function parseTemplate(template) {
	const parts = [];
	const regex = /\{\{(.*?)\}\}/gs;
	let lastIndex = 0;
	for (const match of template.matchAll(regex)) {
		const matchStart = match.index ?? 0;
		const matchEnd = matchStart + match[0].length;
		if (matchStart > lastIndex) parts.push({
			type: "text",
			value: template.slice(lastIndex, matchStart),
			start: lastIndex,
			end: matchStart
		});
		parts.push({
			type: "expression",
			value: (match[1] ?? "").trim(),
			start: matchStart + 2,
			end: matchEnd - 2
		});
		lastIndex = matchEnd;
	}
	if (lastIndex < template.length) parts.push({
		type: "text",
		value: template.slice(lastIndex),
		start: lastIndex,
		end: template.length
	});
	return parts;
}

//#endregion
//#region src/runtime.ts
const { evaluate } = evalESTreeExpr;
var ExpressionRuntime = class {
	#parserOptions;
	#autocompleter;
	#logger;
	constructor(logger = console) {
		this.#parserOptions = {
			ecmaVersion: "latest",
			sourceType: "script",
			allowHashBang: false,
			locations: true
		};
		this.#autocompleter = new AutoComplete(this, logger);
		this.#logger = logger;
	}
	/**
	* Evaluates an expression based on the given inputs/context.
	*/
	evaluate(expr, inputs) {
		try {
			const parsed = this.parse(expr);
			if (parsed.invalidNodes.length > 0) throw new ExpressionError("Invalid nodes found when parsing");
			return evaluate.sync(parsed.result, inputs, {
				functions: true,
				withMembers: true,
				generate: escodegen.generate
			});
		} catch (error) {
			throw error instanceof Error ? new ExpressionError(error.message) : new ExpressionError("Unexpected error");
		}
	}
	/**
	* Evaluates an expression safely by returning the error instead of throwing when invalid.
	*/
	safeEvaluate(expr, inputs) {
		try {
			return { value: this.evaluate(expr, inputs) };
		} catch (error) {
			this.#logger.error(`Error while evaluating expression ${expr}`, error);
			if (error instanceof ExpressionError) return {
				value: void 0,
				error
			};
			return {
				value: void 0,
				error: error instanceof Error ? new ExpressionError(error.message) : new ExpressionError("Unexpected error")
			};
		}
	}
	/**
	* Evaluates a condition safely to a boolean.
	*/
	evaluateBoolean(expr, inputs) {
		if (expr.trim().length === 0) return true;
		const evalResult = this.safeEvaluate(expr, inputs);
		if (typeof evalResult.error !== "undefined") return false;
		return Boolean(evalResult.value);
	}
	/**
	* Evaluates an array of conditions as a single logical expression.
	* The function treats the conditions as if they were joined by an AND operator,
	* meaning the evaluation returns `true` only if all conditions are truthy.
	*/
	evaluateBooleanAll(expressions, inputs) {
		if (expressions.length === 0) return true;
		return expressions.every((expression) => this.evaluateBoolean(expression, inputs));
	}
	/**
	* Parse a template and validate all embedded expressions.
	*/
	parseTemplate(template) {
		const parts = parseTemplate(template);
		const errors = [];
		for (const part of parts) if (part.type === "expression") try {
			const { invalidNodes } = this.parse(part.value);
			if (invalidNodes.length > 0) errors.push(new ExpressionError("Invalid expression"));
		} catch (error) {
			errors.push(error);
		}
		return {
			parts,
			errors
		};
	}
	/**
	* Evaluate a template string containing `{{ expression }}` placeholders.
	*/
	evaluateTemplate(template, inputs) {
		const { parts } = this.parseTemplate(template);
		return parts.map((part) => {
			if (part.type === "text") return part.value;
			const result = this.evaluate(part.value, inputs);
			return formatExpressionResult(result, "");
		}).join("");
	}
	/**
	* Parses a binary expression and returns an @ExpressionParserResult.
	*/
	parse(expr, options = { loose: false }) {
		try {
			const ast = options.loose ? parse$1(expr, { ...this.#parserOptions }) : parse(expr, { ...this.#parserOptions });
			if (!ast.body || ast.body.length === 0) throw new ExpressionError("Empty or invalid expression");
			const firstExprIndex = ast.body.findIndex((node) => isParsedExpressionStatement(node));
			const [statement] = ast.body.splice(firstExprIndex, 1);
			if (!statement || !isParsedExpressionStatement(statement)) throw new ExpressionError("Empty or invalid expression");
			const invalidNodes = ast.body.filter(filterOutModuleDeclarationStatement);
			return {
				result: statement.expression,
				invalidNodes
			};
		} catch (error) {
			if (error instanceof SyntaxError) throw createExpressionErrorFromSyntaxError(expr, error);
			if (error instanceof ExpressionError) throw error;
			throw new ExpressionError("Unexpected error");
		}
	}
	/**
	* Provides autocomplete suggestions for the given expression at the provided cursor offset.
	*/
	autocomplete(expr, cursorOffset, context) {
		return { suggestions: this.#autocompleter.getSuggestions(expr, cursorOffset, context) };
	}
	generate(_node) {
		throw new Error("Not yet implemented");
	}
};
function createExpressionErrorFromSyntaxError(code, error) {
	const loc = error.loc;
	if (!loc) return new ExpressionError(error.message);
	const errorMessage = `${error.message.replace(/\s*\(\d+:\d+\)$/, "")} at ${code.split("\n").length > 1 ? `line ${loc.line}, ` : ""}char ${loc.column}`;
	const token = getTokenAtLoc(code, loc);
	if (!token) return new ExpressionError(errorMessage, loc);
	return new ExpressionError(errorMessage, loc, token);
}
function getTokenAtLoc(code, errorLoc) {
	const tokens = tokenizer(code, {
		ecmaVersion: "latest",
		locations: true
	});
	try {
		for (const token of tokens) {
			if (!token.loc) continue;
			const { start, end } = token.loc;
			const onSameLine = errorLoc.line === start.line;
			const inColumnRange = errorLoc.column >= start.column && errorLoc.column < end.column;
			if (onSameLine && inColumnRange) return token;
		}
	} catch (_error) {
		return;
	}
}
function isParsedExpressionStatement(statement) {
	return statement.type === "ExpressionStatement";
}
function filterOutModuleDeclarationStatement(statement) {
	return ![
		"ImportDeclaration",
		"ExportNamedDeclaration",
		"ExportDefaultDeclaration",
		"ExportAllDeclaration"
	].includes(statement.type);
}

//#endregion
export { ExpressionError, ExpressionRuntime, OptionalFunctionArg, SUPPORTED_BINARY_OPERATORS, SUPPORTED_CONDITIONAL_OPERATORS, SUPPORTED_LOGICAL_OPERATORS, SymbolArray, SymbolBoolean, SymbolError, SymbolFunction, SymbolNull, SymbolNumber, SymbolObject, SymbolString, SymbolType, SymbolUndefined, SymbolUnion, SymbolsTable, filterOutModuleDeclarationStatement, filterOutNullable, formatExpressionResult, inferDefaultInputValuesFromObjectJSONSchema, parseTemplate, resolveSymbolDef };